/**
 * "Needs you": every decision the system is waiting on a person for, in one
 * list, each with the action that settles it.
 *
 * Before this the flags lived in five places — the Money page panels, each
 * athlete page, the Dashboard's contact card, Settings for the iPhone, each
 * contract for document reads — and some could only be read, not settled. A
 * flag nobody can settle in one tap becomes noise; this is where each one is
 * settled.
 *
 * Settling is one of:
 *   - a fix: use the value the sources agree on, reverse booked commission,
 *     record money that arrived, confirm a paycheck schedule, apply a read
 *     contract, accept a contact suggestion;
 *   - a decision to keep what is on file ("Keep ours");
 *   - "Mark checked", for a disagreement a person has looked at and accepts.
 *     It is remembered against that exact finding (cross_checks.acknowledged
 *     on the record): if the finding changes, the item comes back.
 *
 * Pure; the page loads the records and runs it.
 */

import type { SalaryCrossCheck } from './salaryCrossCheck.js'
import type { RecordedCrossCheck } from './recordedCrossCheck.js'
import type { ProfileCrossCheck, DetailField } from './profileCrossCheck.js'
import { DETAIL_LABELS } from './profileCrossCheck.js'
import type { BalanceFinding } from './balances.js'
import type { MoneyItem } from './moneyCalendar.js'
import { honorDetail, honorLinks, type HonorFind } from './honors.js'
import { deadlineWords, NIL_GO_STATUS_LABEL, type NilGoState, type NilGoStatus } from './nilGo.js'

export type NeedsGroup = 'NIL Go' | 'Deals' | 'Honors' | 'Money' | 'Contracts' | 'Athletes' | 'Contacts'

/** Which check on a record a "Mark checked" answers. */
export type CheckName = 'salary' | 'recorded' | 'roster' | `profile:${DetailField}`

export type NeedsAction =
  | { type: 'acknowledge'; label: string; collection: 'contracts' | 'clients'; recordId: string; check: CheckName; key: string }
  | { type: 'use_value'; label: string; clientId: string; field: DetailField; value: string; synced: boolean }
  | { type: 'keep_value'; label: string; clientId: string; field: DetailField; ours: string; espn: string | null; synced: boolean; ack: { check: CheckName; key: string } | null }
  | { type: 'reverse_commission'; label: string; rowId: string }
  | { type: 'log_arrived'; label: string; item: MoneyItem & { client_id: string; client_name: string } }
  | { type: 'confirm_schedule'; label: string; contractId: string; weeks: 34 | 36 }
  | { type: 'contract_read'; label: string; extractionId: string; decision: 'apply' | 'reject' }
  | { type: 'contact_suggestion'; label: string; suggestionId: string; decision: 'accept' | 'skip' }
  | { type: 'book_change'; label: string; changeId: string; decision: 'approve' | 'skip' }
  | { type: 'partner_inquiry'; label: string; inquiryId: string; decision: 'add' | 'dismiss' }
  | { type: 'honor'; label: string; findId: string; decision: 'add' | 'dismiss' }
  | { type: 'nil_go'; label: string; dealId: string; status: NilGoStatus }
  | { type: 'open'; label: string; to: string }

export interface NeedsItem {
  /** Stable for a finding, so a list re-render keeps its place. */
  id: string
  group: NeedsGroup
  client_id: string | null
  client_name: string | null
  title: string
  detail: string
  actions: NeedsAction[]
  /** Where it was said, when the item rests on sources outside Reppr. */
  links?: { label: string; url: string }[]
}

// ── Inputs: the records as the page loads them ──

export interface NeedsClient {
  id: string
  name: string
  cross_checks?: Record<string, any> | null
  profile_sync?: Record<string, any> | null
  /** The record's values for the detail fields. */
  values: Partial<Record<DetailField, string | null>>
}

export interface NeedsContract { id: string; title: string | null; client_id: string | null; cross_checks?: Record<string, any> | null }
export interface NeedsMissed { client_id: string; client_name: string; kind: string; source_id: string; source_title: string | null; date: string; amount: number | null; reason: string }
export interface NeedsGap { client_id: string; client_name: string; kind: string; source_id: string; source_title: string | null; reason: string }
export interface NeedsRead { id: string; contract_id: string; contract_title: string | null; client_id: string | null; client_name: string | null; reasons: string[] }
export interface NeedsSuggestion { id: string; card_name: string; display_value: string; field: string; person: { name: string; entity_type: string; entity_id: string } }
/** A brand's message from a partner page (api/partner-pages.ts?inquiries=1). */
export interface NeedsInquiry { id: string; client_id: string; client_name: string | null; link_label: string | null; name: string; company: string; email: string; phone: string | null; message: string }
export interface NeedsBookChange { id: string; kind: string; display_value: string; card_name: string | null; person: { name: string; entity_type: string; entity_id: string } | null }

/** Fingerprints of a finding: what "Mark checked" is remembered against. */
export const findingKeys = {
  salary: (c: SalaryCrossCheck) => JSON.stringify([c.verdict, c.espn_salary, c.our_base, c.term?.ours ?? null, c.term?.espn ?? null]),
  recorded: (c: RecordedCrossCheck) => c.points.join('|'),
  roster: (c: { points: string[] }) => c.points.join('|'),
  profile: (f: { field: string; record: string | null; espn: string | null; sleeper: string | null }) => JSON.stringify([f.field, f.record, f.espn, f.sleeper]),
}

export function isAcknowledged(crossChecks: Record<string, any> | null | undefined, check: CheckName, key: string): boolean {
  return crossChecks?.acknowledged?.[check]?.key === key
}

/**
 * A contract salary a second source disputes and nobody has marked checked —
 * what Needs you lists, and what holds an athlete's portal invite back.
 */
export function salaryOpen(crossChecks: Record<string, any> | null | undefined): boolean {
  const salary = crossChecks?.salary as SalaryCrossCheck | undefined
  if (!salary) return false
  const wrong = salary.verdict === 'differs' || salary.verdict === 'missing_ours' || salary.term?.agrees === false
  return wrong && !isAcknowledged(crossChecks, 'salary', findingKeys.salary(salary))
}

const SYNCED: DetailField[] = ['position', 'jersey_number', 'height', 'weight']
const money = (n: number | null) => (n == null ? 'an amount nobody has recorded' : `$${(Math.round(n * 100) / 100).toLocaleString('en-US')}`)

export function buildNeedsYou(input: {
  clients: NeedsClient[]
  contracts: NeedsContract[]
  /** Null when this person cannot see the agency's money (assistants). */
  missed: NeedsMissed[] | null
  dueToRecord: Array<MoneyItem & { client_id: string; client_name: string }> | null
  gaps: NeedsGap[] | null
  balances: BalanceFinding[] | null
  reads: NeedsRead[]
  suggestions: NeedsSuggestion[]
  bookChanges: NeedsBookChange[]
  /** New partner inquiries; absent where they were not loaded. */
  inquiries?: NeedsInquiry[]
  /** The weekly sweep's finds nobody has answered yet. */
  honors?: HonorFind[]
  /** College deals NIL Go has something to say about. */
  nilGo?: NilGoState[]
}): NeedsItem[] {
  const items: NeedsItem[] = []

  // ── Deals: a brand wrote in from a partner page. New business first. ──
  for (const q of input.inquiries ?? []) {
    const who = q.client_name ?? 'an athlete'
    const said = q.message.length > 300 ? `${q.message.slice(0, 297)}…` : q.message
    items.push({
      id: `inquiry:${q.id}`, group: 'Deals', client_id: q.client_id, client_name: q.client_name,
      title: `Partner inquiry: ${q.company} for ${who}`,
      detail: `${q.name} (${q.email}${q.phone ? `, ${q.phone}` : ''})${q.link_label ? `, from the link sent to "${q.link_label}"` : ''}: "${said}"`,
      actions: [
        { type: 'partner_inquiry', label: 'Add to pipeline', inquiryId: q.id, decision: 'add' },
        { type: 'partner_inquiry', label: 'Dismiss', inquiryId: q.id, decision: 'dismiss' },
      ],
    })
  }
  const nameOf = new Map(input.clients.map(c => [c.id, c.name]))

  // ── NIL Go: an athlete's eligibility can turn on these, so they come first. ──
  for (const s of input.nilGo ?? []) {
    if (!s.clock_running || s.status === 'cleared' || s.status === 'not_required') continue
    const open: NeedsAction = { type: 'open', label: 'Open deal', to: `/deals/${s.deal_id}` }
    const nil = (label: string, status: NilGoStatus): NeedsAction => ({ type: 'nil_go', label, dealId: s.deal_id, status })
    const deal = s.deal_title ?? 'This deal'
    const heldLine = s.hold ? ' Payments and posted deliverables are on hold until it clears.' : ''
    const common = { group: 'NIL Go' as const, id: `nilgo:${s.deal_id}:${s.status}`, client_id: s.client_id, client_name: nameOf.get(s.client_id) ?? null }
    if (s.status === 'not_reported') {
      items.push({
        ...common,
        title: s.overdue ? `NIL Go report overdue: ${deal}` : `Report to NIL Go: ${deal}`,
        detail: `${deadlineWords(s)} The athlete files the written terms within five business days of agreeing them.${heldLine}`,
        actions: [nil('Athlete reported it', 'reported'), open],
      })
    } else if (s.status === 'reported' || s.status === 'flagged') {
      items.push({
        ...common,
        title: `${NIL_GO_STATUS_LABEL[s.status]}: ${deal}`,
        detail: `${s.reported_on ? `Reported ${s.reported_on}.` : 'Reported.'}${heldLine}`,
        actions: [nil('Cleared', 'cleared'), ...(s.status === 'reported' ? [nil('Flagged for review', 'flagged')] : []), nil('Not cleared', 'not_cleared'), open],
      })
    } else {
      items.push({
        ...common,
        title: `NIL Go did not clear: ${deal}`,
        detail: `Going ahead risks eligibility. The options are to revise and resubmit, cancel and refund any money received, or go to neutral arbitration.${heldLine}`,
        actions: [nil('Revised and resubmitted', 'reported'), open],
      })
    }
  }

  // ── Honors: what the weekly sweep found, for a person to confirm. ──
  for (const h of input.honors ?? []) {
    items.push({
      id: `honor:${h.id}`, group: 'Honors', client_id: h.client_id, client_name: nameOf.get(h.client_id) ?? null,
      title: h.headlines.some(x => x.official) ? 'An honor this week' : 'An honor reported this week, no official source yet',
      detail: honorDetail(h.headlines),
      links: honorLinks(h.headlines),
      actions: [
        { type: 'honor', label: 'Add to record', findId: h.id, decision: 'add' },
        { type: 'honor', label: 'Not an honor', findId: h.id, decision: 'dismiss' },
      ],
    })
  }
  const contractTitle = new Map(input.contracts.map(c => [c.id, c.title]))

  // ── Money ──
  for (const m of input.missed ?? []) {
    const due = (input.dueToRecord ?? []).find(i => i.source_id === m.source_id && i.date === m.date)
    const actions: NeedsAction[] = []
    if (due) actions.push({ type: 'log_arrived', label: `It arrived — ${money(due.amount)}`, item: due })
    actions.push({ type: 'open', label: 'Open money', to: '/revenue' })
    items.push({
      id: `missed:${m.source_id}:${m.date}`, group: 'Money', client_id: m.client_id, client_name: m.client_name,
      title: m.kind === 'deal_payment_unpaid' ? `${m.source_title ?? 'A deal payment'} is past due` : `A paycheck was never recorded`,
      detail: `${m.reason}${m.kind === 'paycheck_unrecorded' ? ` About ${money(m.amount)}.` : ` ${money(m.amount)}.`}`,
      actions,
    })
  }
  for (const f of input.balances ?? []) {
    const actions: NeedsAction[] = []
    if (f.kind === 'booked_on_closed' || f.kind === 'booked_without_source') actions.push({ type: 'reverse_commission', label: `Reverse ${money(f.gap)}`, rowId: f.subject_id })
    actions.push({ type: 'open', label: 'Open money', to: '/revenue' })
    items.push({ id: `books:${f.kind}:${f.subject_id}`, group: 'Money', client_id: f.client_id, client_name: f.client_id ? nameOf.get(f.client_id) ?? null : null, title: 'The books do not balance', detail: f.text, actions })
  }
  for (const g of input.gaps ?? []) {
    if (g.kind !== 'paycheck_schedule') continue
    items.push({
      id: `schedule:${g.source_id}`, group: 'Money', client_id: g.client_id, client_name: g.client_name,
      title: `Confirm the paycheck schedule — ${g.source_title ?? 'contract'}`,
      detail: `${g.reason} Until it is confirmed, none of this contract's checks are projected or recorded.`,
      actions: [
        { type: 'confirm_schedule', label: '36 weeks, weekly', contractId: g.source_id, weeks: 36 },
        { type: 'confirm_schedule', label: '34 weeks, weekly', contractId: g.source_id, weeks: 34 },
        { type: 'open', label: 'Other…', to: `/contracts/${g.source_id}` },
      ],
    })
  }

  // ── Contracts: figures a second source disputes, recorded pay, documents read ──
  for (const c of input.contracts) {
    const who = c.client_id ? nameOf.get(c.client_id) ?? null : null
    const salary = c.cross_checks?.salary as SalaryCrossCheck | undefined
    if (salary) {
      const key = findingKeys.salary(salary)
      if (salaryOpen(c.cross_checks)) {
        const lines = [
          salary.verdict === 'agrees' || salary.verdict === 'missing_espn' ? null : salary.explanation,
          salary.term?.agrees === false ? `The contract has salaries through ${salary.term.ours ?? 'no season at all'}; ESPN has him signed through ${salary.term.espn}.` : null,
        ].filter(Boolean)
        items.push({
          id: `salary:${c.id}`, group: 'Contracts', client_id: c.client_id, client_name: who,
          title: `The contract and ESPN disagree — ${c.title ?? 'contract'}`, detail: lines.join(' '),
          actions: [
            { type: 'open', label: 'Open contract', to: `/contracts/${c.id}` },
            { type: 'acknowledge', label: 'Mark checked', collection: 'contracts', recordId: c.id, check: 'salary', key },
          ],
        })
      }
    }
    const recorded = c.cross_checks?.recorded as RecordedCrossCheck | undefined
    if (recorded?.verdict === 'differs') {
      const key = findingKeys.recorded(recorded)
      if (!isAcknowledged(c.cross_checks, 'recorded', key)) {
        items.push({
          id: `recorded:${c.id}`, group: 'Money', client_id: c.client_id, client_name: who,
          title: 'Recorded pay does not add up', detail: recorded.points.join(' '),
          actions: [
            { type: 'open', label: 'Open money', to: '/revenue' },
            { type: 'acknowledge', label: 'Mark checked', collection: 'contracts', recordId: c.id, check: 'recorded', key },
          ],
        })
      }
    }
  }
  for (const r of input.reads) {
    items.push({
      id: `read:${r.id}`, group: 'Contracts', client_id: r.client_id, client_name: r.client_name,
      title: `A signed contract was read and needs a decision — ${r.contract_title ?? contractTitle.get(r.contract_id) ?? 'contract'}`,
      detail: r.reasons.length ? r.reasons.join('; ') + '.' : 'Its terms differ from what is on file.',
      actions: [
        { type: 'contract_read', label: "Apply the document's terms", extractionId: r.id, decision: 'apply' },
        { type: 'contract_read', label: 'Reject', extractionId: r.id, decision: 'reject' },
        { type: 'open', label: 'Open contract', to: `/contracts/${r.contract_id}` },
      ],
    })
  }

  // ── Athletes: standing and details ──
  for (const c of input.clients) {
    const roster = c.cross_checks?.roster
    if (roster?.verdict === 'differs') {
      const key = findingKeys.roster(roster)
      if (!isAcknowledged(c.cross_checks, 'roster', key)) {
        items.push({
          id: `roster:${c.id}`, group: 'Athletes', client_id: c.id, client_name: c.name,
          title: 'ESPN and Sleeper disagree on where he stands', detail: `${roster.points.join(' ')} The record follows ESPN.`,
          actions: [
            { type: 'open', label: 'Open athlete', to: `/clients/${c.id}` },
            { type: 'acknowledge', label: 'Mark checked', collection: 'clients', recordId: c.id, check: 'roster', key },
          ],
        })
      }
    }
    const profile = c.cross_checks?.profile as ProfileCrossCheck | undefined
    const covered = new Set<DetailField>()
    for (const finding of profile?.findings ?? []) {
      const f = profile!.fields.find(x => x.field === finding.field)
      if (!f) continue
      covered.add(f.field)
      const check: CheckName = `profile:${f.field}`
      const key = findingKeys.profile(f)
      if (isAcknowledged(c.cross_checks, check, key)) continue
      const label = DETAIL_LABELS[f.field]
      const synced = SYNCED.includes(f.field)
      const actions: NeedsAction[] = []
      const agreed = f.verdict === 'record_differs' ? f.espn : f.verdict === 'one_source' ? (f.espn ?? f.sleeper) : null
      if (agreed) {
        actions.push({ type: 'use_value', label: `Use ${agreed}`, clientId: c.id, field: f.field, value: agreed, synced })
        actions.push({ type: 'keep_value', label: `Keep ${f.record ?? 'ours'}`, clientId: c.id, field: f.field, ours: f.record ?? '', espn: f.espn, synced, ack: { check, key } })
      } else {
        actions.push({ type: 'acknowledge', label: 'Mark checked', collection: 'clients', recordId: c.id, check, key })
      }
      actions.push({ type: 'open', label: 'Open athlete', to: `/clients/${c.id}` })
      items.push({ id: `profile:${c.id}:${f.field}`, group: 'Athletes', client_id: c.id, client_name: c.name, title: `Check his ${label}`, detail: finding.text, actions })
    }
    // A record ESPN disagrees with, held by the profile sync, that the cross-check has not already raised.
    for (const field of SYNCED) {
      const review = c.profile_sync?.[field]?.review
      if (!review || covered.has(field)) continue
      const ours = c.values[field] ?? review.reppr ?? null
      items.push({
        id: `review:${c.id}:${field}`, group: 'Athletes', client_id: c.id, client_name: c.name,
        title: `ESPN lists a different ${DETAIL_LABELS[field]}`,
        detail: `ESPN has ${review.espn}; the record has ${ours ?? `no ${DETAIL_LABELS[field]}`}.`,
        actions: [
          { type: 'use_value', label: `Use ${review.espn}`, clientId: c.id, field, value: String(review.espn), synced: true },
          { type: 'keep_value', label: `Keep ${ours ?? 'ours'}`, clientId: c.id, field, ours: ours ?? '', espn: String(review.espn), synced: true, ack: null },
          { type: 'open', label: 'Open athlete', to: `/clients/${c.id}` },
        ],
      })
    }
  }

  // ── Contacts ──
  for (const s of input.suggestions) {
    items.push({
      id: `suggestion:${s.id}`, group: 'Contacts', client_id: s.person.entity_type === 'client' ? s.person.entity_id : null, client_name: s.person.name,
      title: `A ${s.field.replace('_', ' ')} from your contacts for ${s.person.name}`,
      detail: `${s.display_value}, from the card "${s.card_name}".`,
      actions: [
        { type: 'contact_suggestion', label: 'Add it', suggestionId: s.id, decision: 'accept' },
        { type: 'contact_suggestion', label: 'Skip', suggestionId: s.id, decision: 'skip' },
      ],
    })
  }
  for (const b of input.bookChanges) {
    items.push({
      id: `book:${b.id}`, group: 'Contacts', client_id: b.person?.entity_type === 'client' ? b.person.entity_id : null, client_name: b.person?.name ?? b.card_name,
      title: 'A change to your iPhone contacts',
      detail: `${b.kind.replace(/_/g, ' ')}: ${b.display_value}${b.card_name ? ` (card "${b.card_name}")` : ''}.`,
      actions: [
        { type: 'book_change', label: 'Approve', changeId: b.id, decision: 'approve' },
        { type: 'book_change', label: 'Skip', changeId: b.id, decision: 'skip' },
      ],
    })
  }
  return items
}
